import { createAction } from '@reduxjs/toolkit';
import type { DedupeScope } from '@shared/schemas';

import type { DeactivateScope } from '../infrastructure/list-detail.gateway';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out
 * of the slice's reducers: they trigger side effects, not state transitions (the
 * saga owns the resulting `load*` / `mutation*` transitions).
 */

/** Load the list + all admin-visible resources for the given id. */
export const loadRequested = createAction<string>('listDetail/loadRequested');

/** Queue an enrichment session for the given profiles (checkbox mode sets `force`). */
export const enrichmentRequested = createAction<{ profileIds: string[]; force?: boolean }>(
  'listDetail/enrichmentRequested',
);

/** Deactivate the selected profiles (list scope reads the list id from state). */
export const deactivateRequested = createAction<{ profileIds: string[]; scope: DeactivateScope }>(
  'listDetail/deactivateRequested',
);

/** Restore the given removed profiles back into the list. */
export const restoreRequested = createAction<{ profileIds: string[] }>(
  'listDetail/restoreRequested',
);

/**
 * Validate or unvalidate the given profiles as reviewed by an Invyo expert
 * (#671). The validation belongs to the profile, not to this list.
 */
export const expertValidationRequested = createAction<{
  profileIds: string[];
  validated: boolean;
}>('listDetail/expertValidationRequested');

/** Permanently delete the list. */
export const deleteRequested = createAction('listDetail/deleteRequested');

/**
 * Import the emails of a Surf CSV export onto the list's members (#631). Carries
 * the picked `File` for the saga (whitelisted in the store's serializable check);
 * it never enters the state.
 */
export const importEmailsRequested = createAction<File>('listDetail/importEmailsRequested');
/** Count the list's duplicates against the other lists of its licence (#640). */
export const dedupePreviewRequested = createAction<{ scope: DedupeScope }>(
  'listDetail/dedupePreviewRequested',
);

/** Remove the duplicates of the previewed scope (#640). */
export const dedupeConfirmed = createAction('listDetail/dedupeConfirmed');

/** Open the Duplicate dialog: read the names the copy must not take first (#662). */
export const duplicateRequested = createAction('listDetail/duplicateRequested');

/** Create the copy under the name picked in the dialog (#662). */
export const duplicateConfirmed = createAction<{ name: string }>('listDetail/duplicateConfirmed');
